import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { MessageSquare, Send, X, Sparkles, MapPin, Star, Mic, MicOff, Keyboard, Volume2, VolumeX } from "lucide-react";
import { formatMoney } from "../lib/partnerPricing";
import { listingCoverImage } from "../lib/fileUrl";
import { useSpeechRecognition } from "../lib/useSpeechRecognition";
import { useServerSpeech } from "../lib/useServerSpeech";
import { useSpeechSynthesis } from "../lib/useSpeechSynthesis";
import { AI_NAME } from "../lib/brand";
import { BOOKING_AGENT_ENABLED } from "../lib/featureFlags";
import ChatQuoteCard from "./ChatQuoteCard";

const SS_KEY = "zcars_chat_session";
const MODE_KEY = "zcars_chat_mode";

const TEXT_GREETING =
  `Hi, I'm ${AI_NAME} — your Z-Cars concierge. Tell me a city and dates (e.g. "sports cars in Miami June 10–15") and I'll search the fleet for you.`;

const VOICE_GREETING =
  `Hi, I'm ${AI_NAME}. Tap the microphone and tell me a city and dates — for example, sports cars in Miami June tenth through fifteenth.`;

function browserSttAvailable() {
  if (typeof window === "undefined") return false;
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}

function serverSttAvailable() {
  if (typeof window === "undefined") return false;
  return !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);
}

function sttAvailable() {
  // Agent mode prefers MediaRecorder → Gemini (avoids Chrome Web Speech "network" failures).
  if (BOOKING_AGENT_ENABLED) return serverSttAvailable() || browserSttAvailable();
  return browserSttAvailable();
}

function defaultMode() {
  const saved = sessionStorage.getItem(MODE_KEY);
  if (saved === "voice" || saved === "text") return saved;
  if (BOOKING_AGENT_ENABLED && sttAvailable()) return "voice";
  return "text";
}

function getSessionId() {
  let id = sessionStorage.getItem(SS_KEY);
  if (!id) {
    id = `cs_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    sessionStorage.setItem(SS_KEY, id);
  }
  return id;
}

function actionType(action) {
  return (action?.type || action?.action || action?.id || "").toLowerCase();
}

function actionLabel(action) {
  if (action?.label) return action.label;
  switch (actionType(action)) {
    case "sign_in":
      return "Sign in";
    case "pay":
      return "Pay securely";
    case "confirm_prepaid":
      return "Confirm trip";
    case "open_booking":
      return "Open booking";
    case "open_trips":
      return "Open My trips";
    default:
      return action?.title || "Continue";
  }
}

function ChatVehicleCards({ vehicles, search }) {
  if (!vehicles?.length) return null;
  const qs = new URLSearchParams();
  if (search?.city) qs.set("city", search.city);
  if (search?.start_date) qs.set("start", search.start_date);
  if (search?.end_date) qs.set("end", search.end_date);
  if (search?.vehicle_type) qs.set("vehicle_type", search.vehicle_type);
  const browseHref = `/browse${qs.toString() ? `?${qs}` : ""}`;

  return (
    <div className="mt-3 space-y-2" data-testid="ai-chat-vehicles">
      {vehicles.map(v => {
        const detailQs = new URLSearchParams(qs);
        const href = `/vehicles/${v.vehicle_id}${detailQs.toString() ? `?${detailQs}` : ""}`;
        const img = listingCoverImage(v.images, "https://images.unsplash.com/photo-1511919884226-fd3cad34687c?auto=format&fit=crop&w=400&q=80");
        return (
          <Link
            key={v.vehicle_id}
            to={href}
            className="flex gap-3 p-2 rounded-lg border border-[var(--zc-border)] hover:border-[var(--zc-accent)] transition bg-[var(--zc-bg-2)]"
            data-testid={`ai-chat-vehicle-${v.vehicle_id}`}
          >
            <img src={img} alt="" className="w-16 h-12 object-cover rounded-md shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold truncate">{v.year} {v.make} {v.model}</div>
              <div className="zc-meta flex items-center gap-2 mt-0.5">
                <span className="flex items-center gap-0.5"><MapPin size={9} />{v.location_city}</span>
                {v.avg_rating != null && (
                  <span className="flex items-center gap-0.5"><Star size={9} />{v.avg_rating}</span>
                )}
              </div>
              <div className="text-xs font-semibold mt-1 text-[var(--zc-accent)]">
                {formatMoney(v.daily_rate)}/day
              </div>
            </div>
          </Link>
        );
      })}
      <Link to={browseHref} className="text-xs text-[var(--zc-accent)] font-semibold hover:underline">
        View all matches →
      </Link>
    </div>
  );
}

function ChatActionButtons({ actions, onAction }) {
  if (!actions?.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2" data-testid="ai-chat-actions">
      {actions.map((action, i) => {
        const type = actionType(action);
        const primary = type === "pay" || type === "sign_in" || type === "confirm_prepaid";
        return (
          <button
            key={`${type}-${i}`}
            type="button"
            onClick={() => onAction(action)}
            className={"px-3 h-9 text-xs font-semibold " + (primary ? "zc-btn-primary" : "zc-btn-outline-primary")}
            data-testid={`ai-chat-action-${type || "custom"}`}
          >
            {actionLabel(action)}
          </button>
        );
      })}
    </div>
  );
}

export default function AIChat() {
  const location = useLocation();
  const navigate = useNavigate();
  // Keep Ace visible on Stripe return (/book/:id/complete) so post-pay celebration works.
  const onAgentBookingComplete = BOOKING_AGENT_ENABLED
    && /^\/book\/[^/]+\/complete\/?$/.test(location.pathname);
  const hideOnRoute =
    location.pathname.startsWith("/inspect/")
    || (location.pathname.startsWith("/book/") && !onAgentBookingComplete)
    || location.pathname.startsWith("/browse");

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(defaultMode);
  const [speakReplies, setSpeakReplies] = useState(true);
  const [messages, setMessages] = useState(() => [
    {
      role: "assistant",
      content: defaultMode() === "voice" ? VOICE_GREETING : TEXT_GREETING,
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);
  const greetedRef = useRef(false);
  const lastAgentState = useRef(null);
  const lastQuote = useRef(null);
  const { supported: ttsSupported, speaking, speak, stop: stopSpeaking } = useSpeechSynthesis();

  const preferAgent = BOOKING_AGENT_ENABLED;

  const speakIfVoice = useCallback((text) => {
    if (mode === "voice" && speakReplies && ttsSupported && text) speak(text);
  }, [mode, speakReplies, ttsSupported, speak]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || sending) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: trimmed }]);
    setSending(true);

    const postTurn = (path) => axios.post(
      `${API}${path}`,
      { session_id: sessionId.current, message: trimmed },
      { withCredentials: true, timeout: 90000 },
    );

    try {
      let res;
      let usedAgent = preferAgent;
      try {
        res = await postTurn(preferAgent ? "/agent/turn" : "/chat");
      } catch (firstErr) {
        // Prefer agent, but any agent failure must not brick Ace — fall back to /chat.
        if (!preferAgent) throw firstErr;
        usedAgent = false;
        res = await postTurn("/chat");
      }

      const rawReply = res.data?.reply;
      const reply = usedAgent
        ? (rawReply || "I found that — check the details below.")
        : rawReply;
      if (usedAgent) {
        lastAgentState.current = res.data?.state || null;
        if (res.data?.quote) lastQuote.current = res.data.quote;
      }
      const assistantMsg = {
        role: "assistant",
        content: reply,
        vehicles: res.data?.vehicles || [],
        search: res.data?.search || null,
        ...(usedAgent ? {
          quote: res.data?.quote || null,
          actions: Array.isArray(res.data?.actions) ? res.data.actions : [],
        } : {}),
      };
      setMessages(m => [...m, assistantMsg]);
      speakIfVoice(reply);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let errMsg = "Hmm, I had trouble reaching the engine. Try again in a moment.";
      if (status === 503 || detail === "AI chat unavailable — set GEMINI_API_KEY") {
        errMsg = "AI is offline on this environment (missing Gemini key). Browse the fleet in the meantime.";
      } else if (!e?.response) {
        errMsg = "Network error reaching Z-Cars AI. Check your connection and try again.";
      }
      setMessages(m => [...m, { role: "assistant", content: errMsg }]);
      speakIfVoice(errMsg);
    } finally {
      setSending(false);
    }
  }, [sending, speakIfVoice, preferAgent]);

  const onVoiceFinal = useCallback((transcript) => {
    if (!transcript) return;
    if (BOOKING_AGENT_ENABLED) {
      stopSpeaking();
      sendMessage(transcript);
    } else {
      setInput(transcript);
    }
  }, [sendMessage, stopSpeaking]);

  const browserSpeech = useSpeechRecognition({ onFinal: onVoiceFinal });
  const serverSpeech = useServerSpeech({ onFinal: onVoiceFinal });
  // Agent voice uses server Gemini STT; legacy Ace keeps Chrome Web Speech.
  const voiceEngine = BOOKING_AGENT_ENABLED ? serverSpeech : browserSpeech;
  const sttSupported = voiceEngine.supported;
  const listening = voiceEngine.listening;
  const interim = voiceEngine.interim;
  const voiceError = voiceEngine.error;
  const toggleMic = voiceEngine.toggle;
  const stopMic = voiceEngine.stop;
  const clearError = voiceEngine.clearError;
  const transcribing = BOOKING_AGENT_ENABLED ? !!serverSpeech.transcribing : false;

  useEffect(() => {
    sessionStorage.setItem(MODE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, interim]);

  useEffect(() => {
    if (!open) {
      stopMic();
      stopSpeaking();
    }
  }, [open, stopMic, stopSpeaking]);

  useEffect(() => {
    if (mode !== "voice") {
      stopMic();
      stopSpeaking();
    }
  }, [mode, stopMic, stopSpeaking]);

  // Spoken greeting when agent opens in voice mode for the first time this session.
  useEffect(() => {
    if (!BOOKING_AGENT_ENABLED || !open || mode !== "voice") return;
    if (greetedRef.current || !speakReplies || !ttsSupported) return;
    if (messages.length !== 1) return;
    greetedRef.current = true;
    speak(messages[0].content);
  }, [open, mode, speakReplies, ttsSupported, speak, messages]);

  // After Stripe return: celebrate booking completion in the agent panel.
  // BookingComplete writes the flag async after payment confirms — poll on complete route.
  const celebratedRef = useRef(false);
  useEffect(() => {
    if (!BOOKING_AGENT_ENABLED || celebratedRef.current) return;

    const celebrate = () => {
      const done = sessionStorage.getItem("zcars_agent_booking_done");
      if (!done || celebratedRef.current) return false;
      celebratedRef.current = true;
      sessionStorage.removeItem("zcars_agent_booking_done");
      sessionStorage.removeItem("zcars_agent_awaiting_booking");
      const text = "You're booked. Open My trips for details.";
      setOpen(true);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: text,
          actions: [{ type: "open_trips", label: "Open My trips", href: "/dashboard/renter" }],
        },
      ]);
      if (speakReplies && ttsSupported) speak(text);
      return true;
    };

    if (celebrate()) return undefined;
    if (!onAgentBookingComplete) return undefined;
    const interval = setInterval(() => {
      if (celebrate()) clearInterval(interval);
    }, 400);
    const timeout = setTimeout(() => clearInterval(interval), 45000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [location.pathname, location.search, onAgentBookingComplete, speakReplies, ttsSupported, speak]);

  const switchMode = (next) => {
    if (next === mode) return;
    stopMic();
    stopSpeaking();
    clearError();
    setMode(next);
    if (next === "voice" && messages.length === 1 && speakReplies && ttsSupported) {
      setMessages([{ role: "assistant", content: VOICE_GREETING }]);
      speak(VOICE_GREETING);
      greetedRef.current = true;
    }
    if (next === "text" && messages.length === 1) {
      setMessages([{ role: "assistant", content: TEXT_GREETING }]);
    }
  };

  const handleMic = () => {
    if (BOOKING_AGENT_ENABLED && !listening) stopSpeaking(); // barge-in
    if (transcribing) return;
    toggleMic();
  };

  const handleAction = async (action) => {
    const type = actionType(action);
    const href = action?.url || action?.href || action?.path || action?.to;
    if (type === "sign_in") {
      navigate("/login", { state: { authRequired: true } });
      return;
    }
    if (type === "open_trips") {
      navigate(href || "/dashboard/renter");
      return;
    }
    if (type === "pay" && BOOKING_AGENT_ENABLED) {
      if (actionBusy) return;
      setActionBusy(true);
      try {
        const res = await axios.post(
          `${API}/agent/checkout`,
          { session_id: sessionId.current },
          { withCredentials: true },
        );
        const url = res.data?.url;
        if (!url) throw new Error("missing_checkout_url");
        sessionStorage.setItem("zcars_agent_awaiting_booking", "1");
        window.location.assign(url);
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 409 && detail && typeof detail === "object" && detail.code === "quote_changed") {
          const q = detail.quote || null;
          if (q) lastQuote.current = q;
          const msg = detail.message || "Price updated. Tap Pay securely again.";
          const spoken = "Price updated. Tap Pay securely again.";
          setMessages((m) => [
            ...m,
            {
              role: "assistant",
              content: msg,
              quote: q,
              actions: [
                { type: "pay", label: "Pay securely" },
                ...(q?.vehicle_id || lastAgentState.current?.slots?.vehicle_id
                  ? [{
                      type: "open_booking",
                      label: "Open full booking page",
                      href: `/book/${q?.vehicle_id || lastAgentState.current.slots.vehicle_id}`,
                    }]
                  : []),
              ],
            },
          ]);
          speakIfVoice(spoken);
        } else if (status === 403 && detail === "kyc_required") {
          const msg = "Please complete identity verification, then tap Pay securely again.";
          setMessages((m) => [...m, { role: "assistant", content: msg }]);
          speakIfVoice(msg);
          navigate("/profile/settings");
        } else if (status === 401) {
          navigate("/login", { state: { authRequired: true } });
        } else {
          const msg = "Couldn't start checkout. Try again, or open the full booking page.";
          setMessages((m) => [...m, { role: "assistant", content: msg }]);
          speakIfVoice(msg);
        }
      } finally {
        setActionBusy(false);
      }
      return;
    }
    if (type === "pay") {
      if (href) window.location.assign(href);
      else navigate("/profile/bookings");
      return;
    }
    if (type === "confirm_prepaid" && BOOKING_AGENT_ENABLED) {
      if (actionBusy) return;
      const slots = lastAgentState.current?.slots || {};
      const quote = lastQuote.current || {};
      const vehicleId = slots.vehicle_id || quote.vehicle_id;
      const start = slots.start_date;
      const end = slots.end_date;
      if (!vehicleId || !start || !end) {
        const fallback = href || (vehicleId ? `/book/${vehicleId}` : "/profile/bookings");
        if (/^https?:\/\//i.test(fallback)) window.location.assign(fallback);
        else navigate(fallback);
        return;
      }
      setActionBusy(true);
      try {
        await axios.post(
          `${API}/bookings`,
          {
            vehicle_id: vehicleId,
            start_date: start,
            end_date: end,
            pickup_location: slots.pickup_location || quote.pickup_location || undefined,
          },
          { withCredentials: true },
        );
        const msg = "You're booked. Open My trips for details.";
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: msg,
            actions: [{ type: "open_trips", label: "Open My trips", href: "/dashboard/renter" }],
          },
        ]);
        speakIfVoice(msg);
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 403 && detail === "kyc_required") {
          const msg = "Please complete identity verification, then confirm again.";
          setMessages((m) => [...m, { role: "assistant", content: msg }]);
          speakIfVoice(msg);
          navigate("/profile/settings");
        } else if (status === 401) {
          navigate("/login", { state: { authRequired: true } });
        } else {
          const openHref = `/book/${vehicleId}?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
          navigate(openHref);
        }
      } finally {
        setActionBusy(false);
      }
      return;
    }
    if (type === "open_booking") {
      if (href) {
        if (/^https?:\/\//i.test(href)) window.location.assign(href);
        else navigate(href);
      } else if (action?.vehicle_id) {
        const qs = new URLSearchParams();
        if (action.start_date) qs.set("start", action.start_date);
        if (action.end_date) qs.set("end", action.end_date);
        navigate(`/book/${action.vehicle_id}${qs.toString() ? `?${qs}` : ""}`);
      }
      return;
    }
    if (href) {
      if (/^https?:\/\//i.test(href)) window.location.assign(href);
      else navigate(href);
    }
  };

  const send = () => sendMessage(input);

  const voiceReady = sttSupported;
  const agentVoice = BOOKING_AGENT_ENABLED && mode === "voice";

  if (hideOnRoute) return null;

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] right-6 z-50 w-14 h-14 grid place-items-center zc-btn-primary"
        style={{ borderRadius: "9999px" }}
        data-testid="ai-chat-fab"
        aria-label="Open AI chat"
      >
        {open ? <X size={20} /> : <MessageSquare size={20} />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] max-h-[min(520px,calc(100dvh-6rem))] zc-card flex flex-col overflow-hidden"
          data-testid="ai-chat-panel"
        >
          <div className="px-4 py-3 border-b border-[var(--zc-border)] bg-[var(--zc-bg-3)]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-[var(--zc-accent)]" />
                <div className="text-sm font-semibold">{AI_NAME} · Concierge</div>
              </div>
              <span
                className={"dot " + (listening || speaking ? "live" : "pending")}
                title={listening ? "Listening" : speaking ? "Speaking" : "Idle"}
              />
            </div>
            {BOOKING_AGENT_ENABLED && agentVoice && (
              <p className="text-2xs mt-1.5 text-[var(--zc-text-3)]" data-testid="ai-chat-agent-status">
                {listening
                  ? "Listening…"
                  : speaking
                    ? `${AI_NAME} is speaking — tap mic to interrupt`
                    : sending
                      ? "Working on it…"
                      : "Voice ready"}
              </p>
            )}
            <div className="flex gap-1 mt-2.5 p-0.5 rounded-lg bg-[var(--zc-bg-2)] border border-[var(--zc-border)]">
              <button
                type="button"
                onClick={() => switchMode("text")}
                className={"flex-1 h-7 text-caption font-semibold rounded-md flex items-center justify-center gap-1 transition " +
                  (mode === "text" ? "bg-white text-[var(--zc-text)] shadow-sm" : "text-[var(--zc-text-3)]")}
                data-testid="ai-chat-mode-text"
              >
                <Keyboard size={11} /> Text
              </button>
              <button
                type="button"
                onClick={() => switchMode("voice")}
                className={"flex-1 h-7 text-caption font-semibold rounded-md flex items-center justify-center gap-1 transition " +
                  (mode === "voice" ? "bg-white text-[var(--zc-text)] shadow-sm" : "text-[var(--zc-text-3)]")}
                data-testid="ai-chat-mode-voice"
              >
                <Mic size={11} /> Voice
              </button>
            </div>
          </div>

          {BOOKING_AGENT_ENABLED && !sttSupported && (
            <div
              className="px-4 py-2 text-xs text-[var(--zc-text-2)] bg-[var(--zc-accent-soft)] border-b border-[var(--zc-border)]"
              data-testid="ai-chat-stt-fallback"
              role="status"
            >
              Voice isn&apos;t supported in this browser. Text mode works — try Chrome, Edge, or Safari for the mic.
            </div>
          )}

          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4 bg-white" data-testid="ai-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={"text-sm leading-relaxed " + (m.role === "user" ? "text-[var(--zc-text)]" : "text-[var(--zc-text-2)]")}>
                <div className="eyebrow mb-1.5" style={{ color: m.role === "user" ? "var(--zc-text)" : "var(--zc-accent)" }}>
                  {m.role === "user" ? "You" : AI_NAME}
                </div>
                <div className="whitespace-pre-wrap">{m.content}</div>
                {m.role === "assistant" && m.vehicles?.length > 0 && (
                  <ChatVehicleCards vehicles={m.vehicles} search={m.search} />
                )}
                {m.role === "assistant" && m.quote && (
                  <ChatQuoteCard quote={m.quote} />
                )}
                {m.role === "assistant" && m.actions?.length > 0 && (
                  <ChatActionButtons actions={m.actions} onAction={handleAction} />
                )}
              </div>
            ))}
            {(listening || transcribing) && interim && (
              <div className="text-xs text-[var(--zc-text-3)] italic" data-testid="ai-chat-interim">
                {transcribing ? interim : `Hearing: ${interim}…`}
              </div>
            )}
            {transcribing && !interim && (
              <div className="text-xs text-[var(--zc-text-3)]" data-testid="ai-chat-transcribing">
                Transcribing…
              </div>
            )}
            {sending && <div className="text-xs text-[var(--zc-text-3)]">{AI_NAME} is searching…</div>}
            {speaking && <div className="text-xs text-[var(--zc-accent)]">{AI_NAME} is speaking…</div>}
          </div>

          {mode === "text" && (
            <div className="border-t border-[var(--zc-border)] p-3 flex items-center gap-2 bg-white">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") send(); }}
                placeholder="e.g. luxury cars in Miami June 10–15"
                className="zc-input flex-1 h-10 px-3 text-sm"
                data-testid="ai-chat-input"
                aria-label="Message"
              />
              <button onClick={send} disabled={sending} className="w-10 h-10 grid place-items-center zc-btn-primary" data-testid="ai-chat-send">
                <Send size={14} />
              </button>
            </div>
          )}

          {mode === "voice" && (
            <div className="border-t border-[var(--zc-border)] p-3 bg-white" data-testid="ai-chat-voice-bar">
              {!voiceReady ? (
                <p className="text-xs text-[var(--zc-text-3)] text-center py-2">
                  Voice search isn&apos;t supported in this browser. Use Chrome, Edge, or Safari — or switch to Text.
                </p>
              ) : (
                <>
                  <p className="text-2xs text-center text-[var(--zc-text-3)] mb-3">
                    {transcribing
                      ? "Transcribing your request…"
                      : listening
                        ? "Recording… tap mic when done"
                        : speaking && BOOKING_AGENT_ENABLED
                          ? "Speaking… tap mic to barge in"
                          : BOOKING_AGENT_ENABLED
                            ? "Tap mic, speak, tap again — we transcribe on our servers"
                            : input.trim()
                              ? "Check the transcript, then tap send"
                              : "Tap mic, say city & dates, then send"}
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => setSpeakReplies(v => !v)}
                      className="w-9 h-9 grid place-items-center rounded-full border border-[var(--zc-border)] text-[var(--zc-text-3)]"
                      aria-label={speakReplies ? `Mute ${AI_NAME} voice` : `Enable ${AI_NAME} voice`}
                      data-testid="ai-chat-voice-mute"
                    >
                      {speakReplies ? <Volume2 size={14} /> : <VolumeX size={14} />}
                    </button>
                    <button
                      type="button"
                      onClick={handleMic}
                      disabled={sending || transcribing}
                      className={
                        (BOOKING_AGENT_ENABLED ? "w-16 h-16 " : "w-14 h-14 ") +
                        "grid place-items-center rounded-full transition disabled:opacity-50 " +
                        (listening
                          ? "zc-btn-primary animate-pulse ring-4 ring-[var(--zc-accent-soft)]"
                          : speaking && BOOKING_AGENT_ENABLED
                            ? "border-2 border-[var(--zc-warn)] text-[var(--zc-warn)] bg-[var(--zc-bg-3)]"
                            : "border-2 border-[var(--zc-accent)] text-[var(--zc-accent)] bg-[var(--zc-accent-soft)]")
                      }
                      data-testid="ai-chat-mic"
                      aria-label={listening ? "Stop recording" : speaking ? "Interrupt and record" : "Start recording"}
                    >
                      {listening ? <MicOff size={BOOKING_AGENT_ENABLED ? 26 : 22} /> : <Mic size={BOOKING_AGENT_ENABLED ? 26 : 22} />}
                    </button>
                    {!BOOKING_AGENT_ENABLED && (
                      <button
                        type="button"
                        onClick={send}
                        disabled={sending || !input.trim()}
                        className="w-9 h-9 grid place-items-center zc-btn-primary rounded-full disabled:opacity-40"
                        aria-label="Send edited transcript"
                        data-testid="ai-chat-voice-send"
                      >
                        <Send size={14} />
                      </button>
                    )}
                    {BOOKING_AGENT_ENABLED && (
                      <button
                        type="button"
                        onClick={send}
                        disabled={sending || !input.trim()}
                        className="w-9 h-9 grid place-items-center rounded-full border border-[var(--zc-border)] text-[var(--zc-text-3)] disabled:opacity-40"
                        aria-label="Send edited transcript"
                        data-testid="ai-chat-voice-send"
                        title="Send edited transcript"
                      >
                        <Send size={14} />
                      </button>
                    )}
                  </div>
                  {voiceError && (
                    <p className="text-2xs text-center text-[var(--zc-danger,#dc2626)] mt-2" data-testid="ai-chat-voice-error">
                      {voiceError}
                      {/network|unreachable/i.test(voiceError) && (
                        <>
                          {" "}
                          <button
                            type="button"
                            className="underline font-semibold text-[var(--zc-accent)]"
                            onClick={() => switchMode("text")}
                            data-testid="ai-chat-voice-fallback-text"
                          >
                            Use Text
                          </button>
                        </>
                      )}
                    </p>
                  )}
                  {!listening && (
                    <input
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") send(); }}
                      placeholder={
                        BOOKING_AGENT_ENABLED
                          ? (input ? `Edit & send` : "Or type here…")
                          : (input ? `Edit what ${AI_NAME} heard…` : "Transcript appears here after you speak")
                      }
                      className="zc-input w-full h-9 px-3 text-xs mt-3"
                      data-testid="ai-chat-voice-edit"
                      aria-label="Voice transcript"
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
